import React from "react"
import {
  Box,
  Typography,
  Divider as DividerMui,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid2
} from "@mui/material"
import { styled, ThemeProvider } from "@mui/material/styles"
import { QRCodeSVG } from "qrcode.react"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"
import { Conditional, Stack, QRCodeWithIcon } from "src/shared/components"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import systemLogo from "src/assets/images/Logo-2.png"
import receiptTheme from "src/theme/receipt-theme"
import { DEFAULT_ORDER_RECEIPT_TITLE, OrderReceiptBaseProps } from ".."
import { useTypedSelector } from "src/shared/store/store"
// Styled components for the receipt
const ReceiptContainer = styled(Box)(({ theme }) => ({
  width: "80mm", // Standard thermal receipt width
  margin: "0 auto",
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  "@media print": {
    width: "100%",
    height: "100%",
    overflow: "visible",
    breakInside: "avoid",
    pageBreakInside: "avoid",
    padding: theme.spacing(1),
    marginTop: 0
  }
}))

const ReceiptHeader = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(2),
  "@media print": {
    breakInside: "avoid",
    pageBreakInside: "avoid"
  }
}))

const ReceiptTitle = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  marginBottom: theme.spacing(1)
}))

const ReceiptSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "@media print": {
    breakInside: "avoid",
    pageBreakInside: "avoid"
  }
}))

const CompactTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0.4),
  fontSize: "0.75rem"
}))

const TotalRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  // marginTop: theme.spacing(1),
  padding: theme.spacing(0.3, 0)
}))
const Divider = styled(DividerMui)(() => ({
  border: "1px solid",
  borderColor: "#000"
}))
const DividerDotted = styled(DividerMui)(() => ({
  border: `1px dashed #000`
}))

// Main Receipt Component
const OrderReceiptTemplate2: React.FC<OrderReceiptBaseProps> = ({ receiptData, receiptRef, ...rest }) => {
  const auth = useTypedSelector((state) => state?.authentication)
  const showMpWabaQr = ["mp", "mp-uat"].includes(auth?.tenant?.accountId ?? "  ")
  const wabaQr = (
    <QRCodeWithIcon
      value="http://wa.me/+923200446767"
      size={80}
      icon={
        <WhatsAppIcon
          sx={{
            fontSize: 12,
            color: "#000"
          }}
        />
      }
    />
  )
  // Calculate total quantity
  const totalQuantity = receiptData?.items?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0

  return (
    <>
      <ThemeProvider theme={receiptTheme}>
        <ReceiptContainer ref={receiptRef} {...rest}>
          {/* Header Section */}

          <ReceiptHeader>
            {receiptData?.logo && <img style={{ width: "50%", height: "auto" }} src={receiptData.logo} />}

            {receiptData?.companyName && (
              <Typography variant="h6" fontWeight="bold">
                {receiptData.companyName}
              </Typography>
            )}

            {receiptData?.websiteUrl && (
              <Typography variant="subtitle1">
                Shop Online at
                <br />
                {receiptData.websiteUrl}
              </Typography>
            )}
            <Divider sx={{ borderWidth: "2px" }} />

            <ReceiptTitle variant="body2" mt={1}>
              {receiptData?.title ?? DEFAULT_ORDER_RECEIPT_TITLE}
              {receiptData?.isDuplicateSlip ? (
                <>
                  {"-"}
                  <span
                    style={{ border: "1px dashed #000", borderRadius: "6px", padding: "2px 4px", marginLeft: "4px" }}
                  >
                    Duplicate
                  </span>
                </>
              ) : (
                <></>
              )}
            </ReceiptTitle>

            {receiptData?.orderNumber && (
              <Stack variant="rowCenter" spacing={1}>
                <Typography variant="body2">Order No:</Typography>
                <Typography variant="body1">{receiptData.orderNumber}</Typography>
              </Stack>
            )}

            {receiptData?.locationName && (
              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 0.5 }}>
                {receiptData.locationName}
              </Typography>
            )}

            {receiptData?.locationAddress && <Typography variant="subtitle1">{receiptData.locationAddress}</Typography>}
          </ReceiptHeader>

          {/* <Divider /> */}

          {/* Order Info Section */}

          <ReceiptSection>
            <Grid2 container sx={{ fontSize: "0.75rem" }}>
              {receiptData?.orderDate && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Order Date:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.orderDate}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.orderType && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Order Type:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.orderType}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.commercialId && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Commercial ID (POS ID):</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.commercialId}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.registerName && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Register Name:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {" "}
                      {receiptData.registerName}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.cashierName && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Cashier Name:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }} fontWeight={700}>
                    <Typography variant="subtitle1">{receiptData.cashierName}</Typography>
                  </Grid2>
                </>
              )}
            </Grid2>
          </ReceiptSection>

          {/* Customer Info Section */}

          <ReceiptSection>
            <Grid2 container sx={{ fontSize: "0.75rem" }}>
              {receiptData?.customerName && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Customer Name:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.customerName}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.customerPhone && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Customer Phone:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.customerPhone}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.customerEmail && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Customer Email:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.customerEmail}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.customerAddress && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Customer Address:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.customerAddress}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.customerTaxNo && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Customer Tax No.:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.customerTaxNo}
                    </Typography>
                  </Grid2>
                </>
              )}

              {receiptData?.customerCommercialNo && (
                <>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1">Customer Commercial No.:</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {receiptData.customerCommercialNo}
                    </Typography>
                  </Grid2>
                </>
              )}
            </Grid2>
          </ReceiptSection>

          {/* Items Section */}
          {receiptData?.items && receiptData?.items?.length > 0 && (
            <ReceiptSection>
              <TableContainer component={Box} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <CompactTableCell sx={{ p: 0 }} colSpan={3}>
                        <Divider />
                      </CompactTableCell>
                    </TableRow>
                    <TableRow>
                      <CompactTableCell
                        sx={{
                          px: 0,
                          fontWeight: 700
                        }}
                      >
                        Product Name
                      </CompactTableCell>
                      <CompactTableCell sx={{ px: 0, fontWeight: 700, width: "75px" }} align="right">
                        Price
                      </CompactTableCell>
                      <CompactTableCell
                        sx={{
                          px: 0,
                          fontWeight: 700,
                          width: "75px"
                        }}
                        align="right"
                      >
                        Total
                      </CompactTableCell>
                    </TableRow>
                    <TableRow>
                      <CompactTableCell sx={{ p: 0 }} colSpan={3}>
                        <Divider />
                      </CompactTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receiptData?.items?.map((item, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <CompactTableCell sx={{ pl: 0, fontWeight: 700 }}>
                            {`${item.quantity || 1}x ${item.name || "Product"}`}
                          </CompactTableCell>
                          <CompactTableCell align="right" sx={{ fontWeight: 700 }}>
                            {getDisplayPrice(item.unitPrice ?? 0) || 0}
                          </CompactTableCell>
                          <CompactTableCell sx={{ pr: 0, fontWeight: 700 }} align="right">
                            {getDisplayPrice(item.total ?? 0) || 0}
                          </CompactTableCell>
                        </TableRow>
                        <TableRow>
                          <CompactTableCell colSpan={3} sx={{ p: 0 }}>
                            <DividerDotted sx={{ borderColor: "rgba(0,0,0,0.75)" }} />
                          </CompactTableCell>
                        </TableRow>
                        <TableRow>
                          <CompactTableCell sx={{ p: 0 }}>
                            {/* {item.skuCode && (
                              <Typography lineHeight={0} variant="caption" textAlign="left" display="block">
                                sku code: {item.skuCode}
                              </Typography>
                            )} */}
                          </CompactTableCell>
                          <CompactTableCell sx={{ p: 0, pr: 0.2 }}>
                            {item.discount ? (
                              <Typography fontSize="10px" textAlign="right" display="block" fontWeight={700}>
                                {item.discount !== undefined && `Disc: ${getDisplayPrice(item.discount ?? 0)} `}
                              </Typography>
                            ) : (
                              <></>
                            )}
                            {/* {item.beforeDiscount !== undefined && (
                              <Typography fontSize="10px" textAlign="right" display="block" fontWeight={700}>
                                {`w/o disc: ${getDisplayPrice(item.beforeDiscount ?? 0)} `}
                              </Typography>
                            )} */}
                          </CompactTableCell>
                          <CompactTableCell sx={{ p: 0, pl: 0.2 }}>
                            {item.beforeDiscount !== undefined && (
                              <Typography fontSize="10px" textAlign="right" display="block" fontWeight={700}>
                                {item.beforeDiscount !== undefined &&
                                  `Gross: ${getDisplayPrice(item.beforeDiscount ?? 0)}`}
                              </Typography>
                            )}
                          </CompactTableCell>
                        </TableRow>
                        <TableRow>
                          <CompactTableCell sx={{ p: 0 }}>
                            {item.taxRate !== undefined && (
                              <Typography fontSize="10px" textAlign="left" display="block" fontWeight={700}>
                                {`Tax Rate: ${item.taxRate} % `}
                              </Typography>
                            )}
                          </CompactTableCell>
                          <CompactTableCell sx={{ p: 0, pr: 0.2 }}>
                            {item.taxAmount !== undefined && (
                              <Typography fontSize="10px" textAlign="right" display="block" fontWeight={700}>
                                {`Tax: ${getDisplayPrice(item.taxAmount ?? 0)}`}
                              </Typography>
                            )}
                          </CompactTableCell>
                          <CompactTableCell sx={{ p: 0, pl: 0.2 }}>
                            {item.total !== undefined && (
                              <Typography fontSize="10px" textAlign="right" display="block" fontWeight={700}>
                                {`Net: ${getDisplayPrice(item.total ?? 0)}`}
                              </Typography>
                            )}
                          </CompactTableCell>
                        </TableRow>
                        <Conditional condition={index < (receiptData.items?.length ?? 0) - 1}>
                          <TableRow>
                            <CompactTableCell sx={{ p: 0, height: "2px" }} colSpan={3}>
                              <DividerDotted />
                            </CompactTableCell>
                          </TableRow>
                        </Conditional>
                        {/* <CompactTableCell colSpan={3} sx={{ pt: 0, pb: 1 }}>
                            {item.skuCode && (
                              <Typography variant="subtitle2" display="block">
                                sku code: {item.skuCode}
                              </Typography>
                            )}
                            {(item.discount !== undefined || item.taxExclusive !== undefined) && (
                              <Typography variant="subtitle2" display="block">
                                {item.discount !== undefined && `Disc Value: ${getDisplayPrice(item.discount ?? 0)} `}
                                {item.taxExclusive !== undefined && `Tax Exclusive: ${getDisplayPrice(item.taxExclusive ?? 0)}`}
                              </Typography>
                            )}
                            {(item.taxRate !== undefined ||
                              item.beforeDiscount !== undefined ||
                              item.taxAmount !== undefined) && (
                              <Typography variant="subtitle2" display="block">
                                {item.taxRate !== undefined && `Tax Rate: ${item.taxRate} % `}
                                {item.beforeDiscount !== undefined &&
                                  `Before discount: ${getDisplayPrice(item.beforeDiscount ?? 0)} `}
                                {item.taxAmount !== undefined && `Tax Amount: ${getDisplayPrice(item.taxAmount ?? 0)}`}
                              </Typography>
                            )}
                          </CompactTableCell> */}
                        {/* </TableRow> */}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {receiptData?.items && receiptData?.items?.length > 0 && <Divider />}
              <Stack variant="rowCenterSpaceBetween" sx={{ py: 1 }}>
                <Typography variant="subtitle1">No. of Item(s): {receiptData?.items?.length}</Typography>

                <Typography variant="subtitle1">Total Quantity: {totalQuantity}</Typography>
              </Stack>
              {receiptData?.items && receiptData?.items?.length > 0 && <Divider />}
            </ReceiptSection>
          )}

          {/* Summary Section */}
          <ReceiptSection>
            {receiptData?.subtotal !== undefined && (
              <TotalRow>
                <Typography variant="subtitle1" fontWeight={700}>
                  Subtotal:
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {getDisplayPrice(receiptData.subtotal ?? 0)}
                </Typography>
              </TotalRow>
            )}

            {receiptData?.totalDiscount !== undefined && (
              <TotalRow>
                <Typography variant="subtitle1" fontWeight={700}>
                  Total Discount:
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {getDisplayPrice(receiptData.totalDiscount ?? 0)}
                </Typography>
              </TotalRow>
            )}

            {receiptData?.productDiscount && (
              <TotalRow>
                <Typography variant="subtitle1">Product Discount:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(receiptData.productDiscount ?? 0)}</Typography>
              </TotalRow>
            )}

            {receiptData?.orderDiscount && (
              <TotalRow>
                <Typography variant="subtitle1">Order Discount:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(receiptData.orderDiscount ?? 0)}</Typography>
              </TotalRow>
            )}

            {receiptData?.cardDiscount && (
              <TotalRow>
                <Typography variant="subtitle1">Card Discount:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(receiptData.cardDiscount ?? 0)}</Typography>
              </TotalRow>
            )}

            {receiptData?.tax !== undefined && (
              <TotalRow>
                <Typography variant="subtitle1" fontWeight={700}>
                  Tax:
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {getDisplayPrice(receiptData.tax ?? 0)}
                </Typography>
              </TotalRow>
            )}

            {receiptData?.shippingFee !== undefined && receiptData.shippingFee > 0 && (
              <TotalRow>
                <Typography variant="subtitle1">Shipping Fee:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(receiptData.shippingFee ?? 0)}</Typography>
              </TotalRow>
            )}

            {receiptData?.posFee !== undefined && receiptData.posFee > 0 && (
              <TotalRow>
                <Typography variant="subtitle1">POS Fee:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(receiptData.posFee ?? 0)}</Typography>
              </TotalRow>
            )}
            {receiptData?.platformFee !== undefined && receiptData.platformFee > 0 && (
              <TotalRow>
                <Typography variant="subtitle1">Platform Fee:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(receiptData.platformFee ?? 0)}</Typography>
              </TotalRow>
            )}

            {receiptData?.integrationFee !== undefined && receiptData.integrationFee > 0 && (
              <TotalRow>
                <Typography variant="subtitle1">Integration Fee:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(receiptData.integrationFee ?? 0)}</Typography>
              </TotalRow>
            )}

            {receiptData?.total !== undefined && (
              <TotalRow sx={{ fontWeight: "bold", pt: 1 }}>
                <Typography variant="body2" fontWeight={700}>
                  Total: (Tax Inclusive)
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  PKR {getDisplayPrice(receiptData.total ?? 0)}
                </Typography>
              </TotalRow>
            )}

            {receiptData?.paymentMethod && (
              <TotalRow>
                <Typography variant="subtitle1">Payment Method:</Typography>
                <Typography variant="subtitle1">{receiptData?.paymentMethod}</Typography>
              </TotalRow>
            )}

            {/* {receiptData.paymentMethod && (
              <Typography variant="subtitle1">
                {receiptData.paymentMethod} {receiptData.transactionNumber && `Tx no: ${receiptData.transactionNumber}`}
              </Typography>
            )} */}
          </ReceiptSection>

          <Divider sx={{ borderColor: "#000" }} />

          {/* Footer Section */}
          <ReceiptSection sx={{ textAlign: "center" }}>
            {receiptData?.specialInstructions && (
              <Typography variant="subtitle1" gutterBottom>
                Special Instructions: {receiptData.specialInstructions}
              </Typography>
            )}

            {receiptData?.mrNo && (
              <Typography variant="subtitle1" gutterBottom>
                MR #: {receiptData.mrNo}
              </Typography>
            )}

            <Typography variant="subtitle1" gutterBottom>
              Visit us again soon!
            </Typography>

            {receiptData?.showOrderNumberQr && receiptData?.orderNumber ? (
              <Stack mt={2} direction="row" justifyContent="center" alignItems="flex-start" spacing={2}>
                <Stack alignItems="center" spacing={0.5}>
                  {wabaQr}
                </Stack>
                <Stack alignItems="center" spacing={0.5}>
                  <QRCodeSVG value={receiptData.orderNumber} size={80} />
                  <Typography variant="subtitle2">{receiptData.orderNumber}</Typography>
                </Stack>
              </Stack>
            ) : (
              <>
                {showMpWabaQr && (
                  <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>{wabaQr}</Box>
                )}
                {!receiptData?.showOrderNumberQr && receiptData?.orderNumber && (
                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    <QRCodeSVG value={receiptData.orderNumber} size={80} />
                    <Typography variant="subtitle2">{receiptData.orderNumber}</Typography>
                  </Box>
                )}
              </>
            )}

            {receiptData?.integrationPartnerInvoice !== undefined && (
              <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                Integration Partner Invoice Number: {receiptData.integrationPartnerInvoice}
              </Typography>
            )}

            <Stack my={1} spacing={0.5}>
              <Divider />
              <Divider />
            </Stack>
            <Stack variant="columnCenter" mt={0.5}>
              <Typography variant="subtitle2" display="block">
                Powered by
              </Typography>
              <img src={systemLogo} width="100px" />
            </Stack>

            <Typography variant="subtitle2" display="block">
              www.three-shelves.com
            </Typography>
          </ReceiptSection>
        </ReceiptContainer>
      </ThemeProvider>
    </>
  )
}

export default OrderReceiptTemplate2
